// /reset-password?token=... — accept new password via the token
// the user got via email.
//
// Restyled to match /sign-in's hero+card layout. Form logic untouched:
// POST /v1/auth/reset-password { token, new_password } → 200 ok, then
// we bounce to /sign-in. 400/410 → token expired or invalid → user
// has to request a fresh reset.

import { useState, type FormEvent } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useResetPassword } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { Icons } from "@/components/icons";
import { AuthCanvas, HeroPane } from "./sign-up";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
});

function ResetPasswordPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const nav = useNavigate();
  const search = Route.useSearch();
  const reset = useResetPassword();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const passwordOk = password.length >= 8;
  const passwordsMatch = password === confirm;
  const formValid = passwordOk && passwordsMatch && !!search.token;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!formValid) return;
    try {
      await reset.mutateAsync({ token: search.token!, new_password: password });
      await nav({ to: "/sign-in" });
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        if (e.status === 410 || e.status === 400) {
          setErr(tr
            ? "Bağlantının süresi dolmuş veya geçersiz. Yeni bir sıfırlama iste."
            : "This link expired or is invalid. Request a new reset link.");
        } else {
          setErr(e.message);
        }
      } else {
        setErr(tr
          ? "Parola sıfırlanırken bir hata oluştu."
          : "Something went wrong while resetting your password.");
      }
    }
  }

  // No token in URL → friendly error instead of a broken form
  if (!search.token) {
    return (
      <AuthCanvas>
        <HeroPane tr={tr} />
        <div className="card" style={{ padding: 32, background: "var(--surface)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Hata" : "Error"}</div>
          <h2 style={{ fontSize: 24, marginBottom: 12 }}>{tr ? "Bağlantı eksik." : "Missing token."}</h2>
          <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 22, lineHeight: 1.55 }}>
            {tr
              ? "Bu bağlantı geçerli bir sıfırlama tokeni içermiyor. Tekrar deneyebilirsin."
              : "This link doesn't contain a valid reset token. You can try again."}
          </p>
          <Link
            to="/forgot-password"
            className="btn btn-primary"
            style={{ width: "100%", justifyContent: "center", padding: "11px 16px" }}
          >
            {tr ? "Sıfırlama bağlantısı iste" : "Request a new link"}
            <Icons.ArrowRight size={14} />
          </Link>
        </div>
      </AuthCanvas>
    );
  }

  return (
    <AuthCanvas>
      <HeroPane tr={tr} />

      <div className="card" style={{ padding: 32, background: "var(--surface)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Yeni parola" : "New password"}</div>
        <h2 style={{ fontSize: 24, marginBottom: 10 }}>
          {tr ? "Yeni parolanı belirle." : "Set your new password."}
        </h2>
        <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 22, lineHeight: 1.55 }}>
          {tr ? "En az 8 karakter." : "At least 8 characters."}
        </p>

        <form onSubmit={onSubmit}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={tr ? "Yeni parola" : "New password"}
            style={{ marginBottom: password.length > 0 && !passwordOk ? 4 : 10 }}
          />
          {password.length > 0 && !passwordOk ? (
            <div style={{ color: "var(--error)", fontSize: 11, marginBottom: 8 }}>
              {tr ? "En az 8 karakter." : "At least 8 characters."}
            </div>
          ) : null}
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={tr ? "Yeni parola (tekrar)" : "New password (again)"}
            style={{ marginBottom: confirm.length > 0 && !passwordsMatch ? 4 : 18 }}
          />
          {confirm.length > 0 && !passwordsMatch ? (
            <div style={{ color: "var(--error)", fontSize: 11, marginBottom: 14 }}>
              {tr ? "Parolalar eşleşmiyor." : "Passwords don't match."}
            </div>
          ) : null}

          {err ? (
            <div role="alert" style={{ color: "var(--error)", fontSize: 12, marginBottom: 14 }}>
              {err}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!formValid || reset.isPending}
            className="btn btn-primary"
            style={{ width: "100%", justifyContent: "center", padding: "11px 16px", opacity: !formValid || reset.isPending ? 0.6 : 1 }}
          >
            {reset.isPending
              ? (tr ? "Sıfırlanıyor…" : "Resetting…")
              : (tr ? "Parolayı sıfırla" : "Reset password")}
            <Icons.ArrowRight size={14} />
          </button>
        </form>

        <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", fontSize: 12, color: "var(--mu)" }}>
          <Link to="/sign-in" className="btn btn-ghost btn-sm">
            {tr ? "Giriş sayfasına dön" : "Back to sign in"}
          </Link>
        </div>
      </div>
    </AuthCanvas>
  );
}
